import { useEffect } from "react";
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from "react-native-reanimated";
import { useTheme } from "tamagui";

import Pills from "@/assets/icons/Pills";

const HOP_MS = 350;
const REST_MS = 700;
const HOP_HEIGHT = 12;

/**
 * The pills hop, turn a full circle in the air, bounce on landing, rest, and go again.
 * Holds still under reduced motion.
 */
export default function PillLoader({ size = 40 }: { size?: number }) {
  const color = useTheme().fillPrimary?.get();
  const reducedMotion = useReducedMotion();
  const lift = useSharedValue(0);
  const turn = useSharedValue(0);

  useEffect(() => {
    if (reducedMotion) return;
    // Both loops last HOP_MS * 2 + REST_MS, so the turn stays in step with the hop.
    lift.value = withRepeat(
      withSequence(
        withTiming(-HOP_HEIGHT, {
          duration: HOP_MS,
          easing: Easing.out(Easing.quad),
        }),
        withTiming(0, { duration: HOP_MS, easing: Easing.bounce }),
        withTiming(0, { duration: REST_MS }),
      ),
      -1,
    );
    turn.value = withRepeat(
      withSequence(
        withTiming(360, {
          duration: HOP_MS * 2,
          easing: Easing.inOut(Easing.quad),
        }),
        withTiming(360, { duration: REST_MS }),
        // 360° and 0° look the same, so the reset is invisible.
        withTiming(0, { duration: 0 }),
      ),
      -1,
    );
    return () => {
      cancelAnimation(lift);
      cancelAnimation(turn);
    };
  }, [reducedMotion, lift, turn]);

  const style = useAnimatedStyle(() => ({
    transform: [{ translateY: lift.value }, { rotate: `${turn.value}deg` }],
  }));

  return (
    <Animated.View style={style} role="progressbar" aria-label="Loading">
      <Pills color={color} width={size} height={size} />
    </Animated.View>
  );
}
